import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { DomSanitizer, type SafeUrl } from '@angular/platform-browser';
import { StatusChip } from '../status-chip/status-chip';
import {
  CommentThread,
  type CommentRecipientOption,
  type CommentThreadEntry,
  type NewComment,
} from '../comment-thread/comment-thread';
import {
  categoryGradient,
  humanizeValue,
  scoreColor,
  scoreLabel,
  screenColor,
  typeStyle,
} from '../feedback-presentation/feedback-presentation';
import { DEVICE_CANDIDATE_SEPARATOR, browserDisplayName } from '../environment-info/environment-info';

/** The author of a feedback item. */
export interface FeedbackDetailUser {
  name?: string;
  email?: string;
}

/** A downloadable asset attached to the feedback. */
export interface FeedbackDetailAsset {
  name: string;
  url: string;
}

/**
 * The data shown in the feedback detail drawer. A generic, presentational
 * shape (like FeedbackRow) — the host maps its domain Feedback object onto it.
 */
export interface FeedbackDetail {
  id: string;
  element?: string;
  /** Base URL (path shown in the header). */
  url?: string;
  /** Full URL including query parameters. */
  fullUrl?: string;
  status?: string;
  type?: string;
  category?: string;
  /** Score on a 0–10 scale (host converts from the stored 0–1). */
  score?: number | null;
  /** 'mobile' | 'tablet' | 'desktop'. */
  screen?: string;
  createdBy?: FeedbackDetailUser | null;
  /** ISO date string. */
  created?: string;
  observation?: string;
  recommendation?: string;
  reasoning?: string;
  browser?: string;
  os?: string;
  /**
   * Device model name(s) captured by the widget, e.g. "Samsung Galaxy S23", or
   * "iPhone 15 / iPhone 15 Pro" when the model can only be narrowed to a group.
   */
  device?: string;
  resolution?: string;
  /** The browser version (stored as `meta.appVersion`). */
  appVersion?: string;
  /** Version of the feedback widget that submitted the item (absent on AI feedback). */
  widgetVersion?: string;
  orientation?: string;
  assets?: FeedbackDetailAsset[];
  screenshotUrl?: string;
}

/**
 * Right-hand slide-out drawer showing a single feedback item in full: header
 * (id/status/type/screen/element/url), author, observation, recommendation,
 * reasoning, a details grid (category/score/type + captured environment), the
 * full URL, attached assets, and the screenshot. Presentational — the host
 * passes a `feedback` object and handles `close`.
 */
@Component({
  selector: 'fb-feedback-detail',
  imports: [DatePipe, StatusChip, CommentThread],
  templateUrl: './feedback-detail.html',
  styleUrl: './feedback-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'close.emit()' },
})
export class FeedbackDetailDrawer {
  /** The feedback to display; null hides the drawer. */
  readonly feedback = input<FeedbackDetail | null>(null);

  /** Available status values for the status chip's dropdown (e.g. enum values). */
  readonly statuses = input<string[]>([]);

  /** Emitted when the user picks a different status for this feedback. */
  readonly statusChange = output<string>();

  /** The comment thread for this feedback item. */
  readonly comments = input<CommentThreadEntry[]>([]);

  /** True while the thread is loading. */
  readonly commentsLoading = input(false);

  /** True while a comment is being sent. */
  readonly commentPosting = input(false);

  /**
   * Whether the viewer may mark a comment internal. Left false for client-facing
   * hosts (the widget), where the API would refuse a private comment anyway.
   */
  readonly canPostPrivateComment = input(false);

  /**
   * Who can be notified about a comment — passed straight to the thread's picker.
   * Empty (the default, and what the widget does) hides the picker; supplying options
   * makes choosing a recipient required before a comment can be sent.
   */
  readonly commentRecipientOptions = input<CommentRecipientOption[]>([]);

  /**
   * The feedback author's address: badged in that picker, and the default recipient
   * until somebody comments on the item.
   */
  readonly commentRecipientAuthorEmail = input<string | null>(null);

  /**
   * The signed-in user's address. Passed to the thread so the Notify picker defaults
   * to whoever spoke last rather than to the viewer themselves.
   */
  readonly currentUserEmail = input<string | null>(null);

  /**
   * Builds the link the Full URL opens (e.g. the main app adds the widget's activation
   * parameter). Defaults to the URL unchanged; the shown URL and the Sizzy /
   * BrowserStack links are never affected.
   */
  readonly pageLink = input<(pageUrl: string) => string>((pageUrl) => pageUrl);

  /** Emitted when the user sends a comment; the host performs the write. */
  readonly commentPost = output<NewComment>();

  /** Emitted when the user closes the drawer (button, overlay, or Escape). */
  readonly close = output<void>();

  private readonly sanitizer = inject(DomSanitizer);

  protected readonly typeStyle = typeStyle;
  protected readonly categoryGradient = categoryGradient;
  protected readonly screenColor = screenColor;
  protected readonly scoreLabel = scoreLabel;
  protected readonly scoreColor = scoreColor;
  protected readonly humanize = humanizeValue;
  protected readonly browserName = browserDisplayName;

  /**
   * A trusted `sizzy://` deep link that opens the URL in the Sizzy desktop app.
   * Sizzy strips the `sizzy://` prefix and loads the remainder as the URL, so the
   * target keeps its own http(s):// scheme. The value must bypass Angular's URL
   * sanitizer, which would otherwise rewrite the unknown scheme to `unsafe:`.
   */
  protected sizzyUrl(fullUrl: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(`sizzy://${fullUrl}`);
  }

  /** BrowserStack Live desktop screen resolutions we snap a capture to. */
  private readonly browserStackResolutions = [
    { width: 1024, height: 768 },
    { width: 1280, height: 800 },
    { width: 1280, height: 1024 },
    { width: 1366, height: 768 },
    { width: 1440, height: 900 },
    { width: 1680, height: 1050 },
    { width: 1920, height: 1080 },
    { width: 2048, height: 1536 },
  ];

  /**
   * A BrowserStack Live "integration URL" that opens the feedback's page on the
   * closest environment we can derive from the captured meta. iOS/Android
   * captures open on a real mobile device (`device`); everything else opens on
   * a desktop (browser + version, OS, screen size). BrowserStack snaps any value
   * it can't match exactly to the nearest it supports; `start=true`
   * auto-launches the session. It's a plain https URL, so no sanitizer bypass is
   * needed.
   */
  protected browserStackUrl(item: FeedbackDetail): string {
    const target = item.fullUrl ?? item.url ?? '';
    const mobileOs = this.mobileBrowserStackOs(item.os);

    const params: Array<[string, string]> = [];
    if (mobileOs) {
      params.push(['os', mobileOs]);
      params.push(['device', this.browserStackDevice(mobileOs, item)]);
    } else {
      const { os, osVersion } = this.mapBrowserStackOs(item.os);
      const { name, version } = this.parseBrowser(item.browser, item.appVersion);
      params.push(['os', os], ['os_version', osVersion], ['browser', name]);
      if (version) {
        params.push(['browser_version', version]);
      }
      params.push(['resolution', this.snapBrowserStackResolution(item.resolution)]);
      params.push(['scale_to_fit', 'true']);
    }
    params.push(['start', 'true']);
    params.push(['url', target]);

    const hash = params.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&');
    return `https://live.browserstack.com/dashboard#${hash}`;
  }

  /** The BrowserStack mobile OS for a captured OS string, or null for desktops. */
  private mobileBrowserStackOs(rawOs: string | undefined): 'iOS' | 'Android' | null {
    const value = (rawOs ?? '').toLowerCase();
    if (/ios|iphone|ipad/.test(value)) {
      return 'iOS';
    }
    if (/android/.test(value)) {
      return 'Android';
    }
    return null;
  }

  /**
   * The BrowserStack device to open a mobile capture on: the captured model
   * (the newest of its candidates when only a group is known), or a common
   * phone/tablet for the platform when no usable model was captured.
   */
  private browserStackDevice(mobileOs: 'iOS' | 'Android', item: FeedbackDetail): string {
    const candidates = (item.device ?? '').split(DEVICE_CANDIDATE_SEPARATOR).filter(Boolean);
    const newestCandidate = candidates[candidates.length - 1] ?? '';
    const isTablet = item.screen === 'tablet';

    if (mobileOs === 'iOS') {
      // A bare "iPhone"/"iPad" (model unknown) isn't a BrowserStack device name.
      if (/^(iphone|ipad)\s\S/i.test(newestCandidate)) {
        return newestCandidate;
      }
      return isTablet ? 'iPad 10th' : 'iPhone 15';
    }
    // An unmapped Samsung model number ("Samsung SM-…") isn't a BrowserStack device name.
    if (newestCandidate && !/^samsung sm-/i.test(newestCandidate)) {
      return newestCandidate;
    }
    return isTablet ? 'Samsung Galaxy Tab S9' : 'Samsung Galaxy S23';
  }

  /** Map a captured platform string onto a BrowserStack Live desktop OS. */
  private mapBrowserStackOs(rawOs: string | undefined): { os: string; osVersion: string } {
    const value = (rawOs ?? '').toLowerCase();
    if (/mac|os x|darwin/.test(value)) {
      return { os: 'OS X', osVersion: 'Sonoma' };
    }
    // Windows is the safe default; the AI capture runs on Linux, which
    // BrowserStack Live has no desktop equivalent for.
    return { os: 'Windows', osVersion: '11' };
  }

  /**
   * Parse a captured browser string into a BrowserStack browser name + major
   * version. Handles the AI capture's Selenium "chrome/124.0…" form, the
   * widget's `detect-browser` names (`ios`, `crios`, … — normalized first), and
   * a full user-agent string. When the browser string carries no version, the
   * separately captured `appVersion` (the browser version) is used.
   */
  private parseBrowser(
    rawBrowser: string | undefined,
    appVersion: string | undefined,
  ): { name: string; version: string } {
    const value = browserDisplayName(rawBrowser);
    const lower = value.toLowerCase();

    // Order matters: Edge/Opera UAs also contain "chrome"/"safari".
    let name = 'Chrome';
    let versionPattern = /(?:chrome|crios)\/(\d+)/i;
    if (/edg/.test(lower)) {
      name = 'Edge';
      versionPattern = /edg(?:e|ios|a)?\/(\d+)/i;
    } else if (/firefox|fxios/.test(lower)) {
      name = 'Firefox';
      versionPattern = /(?:firefox|fxios)\/(\d+)/i;
    } else if (/opr|opera/.test(lower)) {
      name = 'Opera';
      versionPattern = /(?:opr|opera)\/(\d+)/i;
    } else if (/chrome|crios/.test(lower)) {
      name = 'Chrome';
    } else if (/safari/.test(lower)) {
      name = 'Safari';
      versionPattern = /version\/(\d+)/i;
    }

    const versionMatch = value.match(versionPattern) ?? (appVersion ?? '').match(/^(\d+)/);
    return { name, version: versionMatch ? `${versionMatch[1]}.0` : '' };
  }

  /** Snap a captured "<w>px * <h>px" viewport to the nearest BrowserStack resolution that fits. */
  private snapBrowserStackResolution(rawResolution: string | undefined): string {
    const sorted = [...this.browserStackResolutions].sort(
      (first, second) => first.width * first.height - second.width * second.height,
    );
    const dimensions = (rawResolution ?? '').match(/\d+/g);
    if (!dimensions || dimensions.length < 2) {
      return '1920x1080';
    }
    const capturedWidth = Number(dimensions[0]);
    const capturedHeight = Number(dimensions[1]);
    const fit =
      sorted.find((option) => option.width >= capturedWidth && option.height >= capturedHeight) ??
      sorted[sorted.length - 1];
    return `${fit.width}x${fit.height}`;
  }

  /** Whether the feedback was authored by the AI reviewer (vs. a person). */
  protected isAiAuthor(item: FeedbackDetail): boolean {
    return item.type === 'ai_feedback';
  }

  /** Up-to-two initials for the author avatar. */
  protected initials(name: string | undefined | null): string {
    if (!name) {
      return '?';
    }
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word[0] ?? '')
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }
}
